import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import CustomButton from '../Button/CustomButton';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useThemeContext } from '../../hook/useThemeManager';
import { COMPANY_BRAND_ON_ORANGE, COMPANY_BRAND_ORANGE } from '../../constants/companyBrand';

interface IPagination {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    hasNextPage?: boolean;
    hasPreviousPage?: boolean;
    /** Company profile: Previous outlined + orange border, Next filled #FF9C28 */
    variant?: 'default' | 'company';
}

const Pagination: React.FC<IPagination> = ({
    currentPage,
    totalPages,
    onPageChange,
    hasNextPage = true,
    hasPreviousPage = true,
    variant = 'default',
}) => {
    const { themeStyle } = useThemeContext();
    const prevDisabled = !hasPreviousPage || currentPage <= 1;
    const nextDisabled = !hasNextPage || currentPage >= totalPages;

    if (variant === 'company') {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    mt: 4,
                    mb: 2,
                    width: '100%',
                    py: 2,
                }}>
                <Button
                    variant="outlined"
                    disabled={prevDisabled}
                    onClick={() => onPageChange(currentPage - 1)}
                    sx={{
                        minWidth: 120,
                        height: 44,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 700,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: '0.875rem',
                        borderColor: COMPANY_BRAND_ORANGE,
                        color: '#ffffff',
                        bgcolor: 'rgba(255,255,255,0.08)',
                        '&:hover': {
                            borderColor: COMPANY_BRAND_ORANGE,
                            bgcolor: 'rgba(255,255,255,0.12)',
                            color: '#ffffff',
                        },
                        '&.Mui-disabled': {
                            borderColor: 'rgba(255,156,40,0.35)',
                            color: 'rgba(255,255,255,0.38)',
                            WebkitTextFillColor: 'rgba(255,255,255,0.38)',
                        },
                    }}>
                    Previous
                </Button>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.BOLD_HEADER,
                        fontSize: '1rem',
                        fontWeight: 700,
                        color: theme[themeStyle].tag_white,
                        textAlign: 'center',
                    }}>
                    Page {currentPage}
                </Typography>
                <Button
                    disabled={nextDisabled}
                    onClick={() => onPageChange(currentPage + 1)}
                    sx={{
                        minWidth: 120,
                        height: 44,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 700,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: '0.875rem',
                        bgcolor: COMPANY_BRAND_ORANGE,
                        color: COMPANY_BRAND_ON_ORANGE,
                        boxShadow: 'none',
                        '&:hover': {
                            bgcolor: '#e88f22',
                            boxShadow: 'none',
                            color: COMPANY_BRAND_ON_ORANGE,
                        },
                        '&.Mui-disabled': {
                            bgcolor: 'rgba(255,156,40,0.35)',
                            color: 'rgba(0,0,0,0.45)',
                            WebkitTextFillColor: 'rgba(0,0,0,0.45)',
                        },
                    }}>
                    Next
                </Button>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                mt: 4,
                mb: 2,
                width: '100%',
            }}>
            <CustomButton
                label="Previous"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={prevDisabled}
                sx={{
                    minWidth: '120px',
                    height: '40px',
                    fontSize: '0.875rem',
                    fontFamily: FONT_ENUM.MEDIUM_TEXT,
                    opacity: prevDisabled ? 0.5 : 1,
                    '&:hover': {
                        WebkitTextFillColor: theme[themeStyle].primary_orange,
                    },
                }}
            />

            <Typography
                sx={{
                    fontFamily: FONT_ENUM.BOLD_HEADER,
                    fontSize: '1rem',
                    color: theme[themeStyle].tag_white,
                    minWidth: '60px',
                    textAlign: 'center',
                }}>
                Page {currentPage}
            </Typography>

            <CustomButton
                label="Next"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={nextDisabled}
                sx={{
                    minWidth: '120px',
                    height: '40px',
                    fontSize: '0.875rem',
                    fontFamily: FONT_ENUM.MEDIUM_TEXT,
                    opacity: nextDisabled ? 0.5 : 1,
                    '&:hover': {
                        WebkitTextFillColor: theme[themeStyle].primary_orange,
                    },
                }}
            />
        </Box>
    );
};

export default Pagination;
